import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  updateAIPersona,
  updateAskPunttSkill,
  updateAskPunttEnabled,
  updateAskPunttConnector,
  createCustomWorkflow,
  updateCustomWorkflow,
  deleteCustomWorkflow,
} from "../../services/brand-guidelines";
import {
  confirmSpreadsheetImport,
  createKnowledgeDataModel,
  createKnowledgeDataRecord,
  deleteKnowledgeDataModel,
  deleteKnowledgeDataRecord,
  getEnterpriseMemories,
  getKnowledgeDatabaseModelRecords,
  getKnowledgeDataRecord,
  getSpreadsheetImportJob,
  startSpreadsheetImport,
  updateKnowledgeDataModel,
  updateKnowledgeDataRecord,
} from "../../services/knowledge-db";
import { isNil } from "../fp";

export type EnterpriseMemoriesQueryArgs = {
  type?: string;
  query?: string;
  pageSize?: number;
};

/** Page-1 fetch for the Memories tab. Keyed by type + query so changing either
 * refetches from the top; the panel accumulates later pages in local state. */
export function useGetEnterpriseMemoriesQuery(
  args: EnterpriseMemoriesQueryArgs,
) {
  const { type, query, pageSize } = args;
  return useQuery({
    queryKey: [
      "enterprise-memories",
      type ?? null,
      query ?? null,
      pageSize ?? null,
    ],
    queryFn: () => getEnterpriseMemories({ type, query, pageSize }),
  });
}

/** Imperative load-more for the Memories tab (dedicated react-query path — does
 * NOT touch the Redux knowledge-database slice or the model-records endpoint). */
export function useLoadMoreEnterpriseMemoriesMutation() {
  return useMutation({
    mutationKey: ["enterprise-memories", "load-more"],
    mutationFn: getEnterpriseMemories,
  });
}

export function useGetKnowledgeDataRecord(knowledgeDataRecordId?: string) {
  const query = useQuery({
    queryKey: ["knowledge-data-record", knowledgeDataRecordId],
    queryFn: () => getKnowledgeDataRecord(knowledgeDataRecordId!),
    enabled: !isNil(knowledgeDataRecordId),
  });

  return query;
}

export function useCreateModelMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-model", "create"],
    mutationFn: createKnowledgeDataModel,
  });
}

export function useUpdateModelMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-model", "update"],
    mutationFn: updateKnowledgeDataModel,
  });
}

export function useDeleteModelMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-model", "delete"],
    mutationFn: deleteKnowledgeDataModel,
  });
}

export function useGetKnowledgeDataRecordMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-record"],
    mutationFn: getKnowledgeDatabaseModelRecords,
  });
}

export function useCreateRecordMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-record", "create"],
    mutationFn: createKnowledgeDataRecord,
  });
}

export function useUpdateRecordMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-record", "update"],
    mutationFn: updateKnowledgeDataRecord,
  });
}

export function useDeleteRecordMutation() {
  return useMutation({
    mutationKey: ["knowledge-data-record", "delete"],
    mutationFn: deleteKnowledgeDataRecord,
  });
}

export function useUpdatePersonaMutation() {
  return useMutation({
    mutationKey: ["ai-personas", "update"],
    mutationFn: updateAIPersona,
  });
}

export function useUpdateAskPunttSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["askpuntt-skills", "update"],
    mutationFn: updateAskPunttSkill,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useUpdateAskPunttEnabledMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["askpuntt-enabled", "update"],
    mutationFn: updateAskPunttEnabled,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useUpdateAskPunttConnectorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["askpuntt-connectors", "update"],
    mutationFn: updateAskPunttConnector,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useCreateCustomWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["custom-workflows", "create"],
    mutationFn: createCustomWorkflow,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useUpdateCustomWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["custom-workflows", "update"],
    mutationFn: updateCustomWorkflow,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useDeleteCustomWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["custom-workflows", "delete"],
    mutationFn: deleteCustomWorkflow,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useStartSpreadsheetImportMutation() {
  return useMutation({
    mutationKey: ["spreadsheet-import", "start"],
    mutationFn: ({
      modelId,
      payload,
    }: {
      modelId: string;
      payload: { s3Key: string; fileName: string };
    }) => startSpreadsheetImport(modelId, payload),
  });
}

export function useGetSpreadsheetImportJobQuery(
  modelId: string | undefined,
  jobId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["spreadsheet-import", modelId, jobId],
    queryFn: () => getSpreadsheetImportJob(modelId!, jobId!),
    enabled: enabled && modelId != null && jobId != null,
    // Poll on a fixed interval while the query is enabled (preview/import phases).
    // Do not gate on cached status — after confirm the cache may still say
    // preview_ready until the next fetch, which previously stopped polling.
    refetchInterval: enabled ? 2000 : false,
  });
}

export function useConfirmSpreadsheetImportMutation() {
  return useMutation({
    mutationKey: ["spreadsheet-import", "confirm"],
    mutationFn: ({
      modelId,
      jobId,
      payload,
    }: {
      modelId: string;
      jobId: string;
      payload?: { applyProposedProperties?: boolean };
    }) => confirmSpreadsheetImport(modelId, jobId, payload),
  });
}
